import React, { useState, useRef, useEffect } from "react";
import {
  Label,
  Input,
  Form,
  Row,
  Col,
  TabPane,
  FormGroup,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
} from "reactstrap";
import { useFormik } from "formik";
import { Cropper } from "react-cropper";
import "cropperjs/dist/cropper.css";
import * as Yup from "yup";
import { createOwner, getProfile } from "../../../api/boat_owner/boatOwnerApi";
import Swal from "sweetalert2";
import "./profile.css";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../ExtraPages/firebaseConfig";
import { useDropzone } from "react-dropzone";

// Validation schema aligned with Mongoose schema
const validationSchema = Yup.object({
  firstName: Yup.string().required("First name is required"),
  lastName: Yup.string().required("Last name is required"),
  address: Yup.string().required("Address is required"),
  contactNumber: Yup.string()
    .matches(/^[0-9]{10}$/, "Enter a valid 10-digit mobile number")
    .required("Mobile number is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  AboutMe: Yup.string(),
  Vessels: Yup.array().of(Yup.string()),
  Categories: Yup.array().of(Yup.string()),
  FishingMethods: Yup.array().of(Yup.string()),
  VesselSafetyCertification: Yup.string(),
  EnvironmentComplianceCertificate: Yup.string(),
  views: Yup.string(),
  currentPassword: Yup.string(),
  twoStepVerification: Yup.boolean(),
});

const BoatOwnerForm = ({ onPercentageChange }) => {
  const [profileImage, setProfileImage] = useState("");
  const [cropImage, setCropImage] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [userId, setUserId] = useState(null);
  const [vesselSafetyPdf, setVesselSafetyPdf] = useState(null);
  const [envCompliancePdf, setEnvCompliancePdf] = useState(null);
  const cropperRef = useRef(null);

  // Fetch userId from Firebase Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUserId(user ? user.uid : null);
    });
    return () => unsubscribe();
  }, []);

  // Function to convert URL to Base64
  const urlToBase64 = async (url) => {
    if (!url || !url.startsWith("http")) return url || "";
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (err) {
      console.error("Failed to convert URL to Base64:", err);
      return "";
    }
  };

  // Fetch profile data when userId is available
  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) return;

      try {
        const response = await getProfile(userId);
        const profile = response.data.data;
        console.log("Fetched profile data:", profile);

        // Convert server URLs to Base64 for PDFs
        const vesselSafetyBase64 = await urlToBase64(
          profile.VesselSafetyCertification
        );
        const envComplianceBase64 = await urlToBase64(
          profile.EnvironmentComplianceCertificate
        );

        const values = {
          firstName: profile.firstName || "",
          lastName: profile.lastName || "",
          contactNumber: profile.contactNumber || "",
          address: profile.address || "",
          email: profile.email || "",
          AboutMe: profile.AboutMe || "",
          Vessels: profile.Vessels || [],
          Categories: profile.Categories || [],
          FishingMethods: profile.FishingMethods || [],
          VesselSafetyCertification: vesselSafetyBase64,
          EnvironmentComplianceCertificate: envComplianceBase64,
          views: profile.views || "",
          twoStepVerification: profile.twoStepVerification || false,
        };
        formik.setValues(values);
        setProfileImage(profile.profileImage || "");
        setVesselSafetyPdf(vesselSafetyBase64);
        setEnvCompliancePdf(envComplianceBase64);
        console.log("Updated vesselSafetyPdf:", vesselSafetyBase64);
        console.log("Updated envCompliancePdf:", envComplianceBase64);

        // Calculate completion percentage
        const totalFields = Object.keys(values).length;
        const filledFields = Object.values(values).filter(
          (value) => value && (Array.isArray(value) ? value.length > 0 : true)
        ).length;
        const percentage = Math.round((filledFields / totalFields) * 100);
        onPercentageChange?.(percentage);
      } catch (err) {
        console.error("Failed to fetch profile:", err);
        Swal.fire("Error!", "Failed to fetch profile data.", "error");
      }
    };

    fetchProfile();
  }, [userId]);

  const toggleCropModal = () => setIsCropModalOpen(!isCropModalOpen);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setCropImage(reader.result);
        toggleCropModal();
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrop = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      const croppedCanvas = cropper.getCroppedCanvas({
        width: 300,
        height: 300,
      });
      const compressedDataUrl = croppedCanvas.toDataURL("image/jpeg", 0.7);
      setProfileImage(compressedDataUrl);
      toggleCropModal();
    }
  };

  // Dropzone handlers
  const handleSafetyPdfDrop = (acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      if (fileRejections[0].errors.some((e) => e.code === "file-too-large")) {
        Swal.fire("Error!", "File size exceeds 10MB limit.", "error");
      } else {
        Swal.fire("Error!", "Please upload a valid PDF file.", "error");
      }
      return;
    }

    const file = acceptedFiles[0];
    if (file && file.type === "application/pdf") {
      const reader = new FileReader();
      reader.onload = () => {
        console.log("Uploading VesselSafetyCertification:", reader.result);
        setVesselSafetyPdf(reader.result);
        formik.setFieldValue("VesselSafetyCertification", reader.result);
        console.log(
          "Current EnvironmentComplianceCertificate:",
          formik.values.EnvironmentComplianceCertificate
        );
      };
      reader.readAsDataURL(file);
    }
  };

  const handleEnvPdfDrop = (acceptedFiles, fileRejections) => {
    if (fileRejections.length > 0) {
      if (fileRejections[0].errors.some((e) => e.code === "file-too-large")) {
        Swal.fire("Error!", "File size exceeds 10MB limit.", "error");
      } else {
        Swal.fire("Error!", "Please upload a valid PDF file.", "error");
      }
      return;
    }

    const file = acceptedFiles[0];
    if (file && file.type === "application/pdf") {
      const reader = new FileReader();
      reader.onload = () => {
        console.log(
          "Uploading EnvironmentComplianceCertificate:",
          reader.result
        );
        setEnvCompliancePdf(reader.result);
        formik.setFieldValue("EnvironmentComplianceCertificate", reader.result);
        console.log(
          "Current VesselSafetyCertification:",
          formik.values.VesselSafetyCertification
        );
      };
      reader.readAsDataURL(file);
    }
  };

  const {
    getRootProps: getSafetyRootProps,
    getInputProps: getSafetyInputProps,
  } = useDropzone({
    onDrop: handleSafetyPdfDrop,
    accept: { "application/pdf": [".pdf"] },
    maxFiles: 1,
    maxSize: 10 * 1024 * 1024, // 10MB in bytes
    noClick: false, // Allow clicking to open file dialog
    noDrag: false, // Allow drag-and-drop
  });

  const { getRootProps: getEnvRootProps, getInputProps: getEnvInputProps } =
    useDropzone({
      onDrop: handleEnvPdfDrop,
      accept: { "application/pdf": [".pdf"] },
      maxFiles: 1,
      maxSize: 10 * 1024 * 1024, // 10MB in bytes
      noClick: false, // Allow clicking to open file dialog
      noDrag: false, // Allow drag-and-drop
    });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      contactNumber: "",
      address: "",
      email: "",
      AboutMe: "",
      Vessels: [],
      Categories: [],
      FishingMethods: [],
      VesselSafetyCertification: "",
      EnvironmentComplianceCertificate: "",
      views: "",
      currentPassword: "",
      twoStepVerification: false,
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!userId) {
        Swal.fire("Error!", "User not authenticated.", "error");
        return;
      }

      try {
        const payload = { ...values, profileImage, userId };
        console.log("Submitting payload:", payload);
        await createOwner(payload);
        setSubmitted(true);
        Swal.fire("Success!", "Profile saved successfully.", "success");

        // Recalculate completion percentage
        const totalFields = Object.keys(values).length;
        const filledFields = Object.values(values).filter(
          (value) => value && (Array.isArray(value) ? value.length > 0 : true)
        ).length;
        const percentage = Math.round((filledFields / totalFields) * 100);
        onPercentageChange?.(percentage);

        // Reset submitted state after a short delay
        setTimeout(() => setSubmitted(false), 2000);
      } catch (error) {
        console.error("Error saving profile:", error);
        Swal.fire(
          "Error!",
          error.response?.data?.message || "Failed to save profile.",
          "error"
        );
      }
    },
  });

  // Helper functions for array inputs
  const handleArrayChange = (field, index, value) => {
    const newArray = [...formik.values[field]];
    newArray[index] = value;
    formik.setFieldValue(field, newArray);
  };

  const addArrayItem = (field) => {
    formik.setFieldValue(field, [...formik.values[field], ""]);
  };

  const removeArrayItem = (field, index) => {
    const newArray = formik.values[field].filter((_, i) => i !== index);
    formik.setFieldValue(field, newArray);
  };

  return (
    <React.Fragment>
      <TabPane tabId="2">
        <Card
          className="overview-content shadow-sm rounded-2xl w-100"
          style={{ maxWidth: "720px", border: "none", marginTop: "0px" }}
        >
          <CardBody className="p-4">
            <h5
              className="fs-18 fw-bold mb-3"
              style={{
                fontSize: "16px",
                fontWeight: "400",
                fontFamily: "poppins",
              }}
            >
              Boat Owner Profile
            </h5>
            <Form onSubmit={formik.handleSubmit}>
              <Row>
                <Col lg={6}>
                  <FormGroup>
                    <Label
                      htmlFor="firstName"
                      style={{
                        fontSize: "14px",
                        fontWeight: "400",
                        fontFamily: "poppins",
                      }}
                    >
                      First Name
                    </Label>
                    <Input
                      name="firstName"
                      type="text"
                      className={`form-control ${
                        formik.errors.firstName && formik.touched.firstName
                          ? "is-invalid"
                          : ""
                      }`}
                      {...formik.getFieldProps("firstName")}
                    />
                    {formik.errors.firstName && formik.touched.firstName && (
                      <div className="invalid-feedback">
                        {formik.errors.firstName}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col lg={6}>
                  <FormGroup>
                    <Label
                      htmlFor="lastName"
                      style={{
                        fontSize: "14px",
                        fontWeight: "400",
                        fontFamily: "poppins",
                      }}
                    >
                      Last Name
                    </Label>
                    <Input
                      name="lastName"
                      type="text"
                      className={`form-control ${
                        formik.errors.lastName && formik.touched.lastName
                          ? "is-invalid"
                          : ""
                      }`}
                      {...formik.getFieldProps("lastName")}
                    />
                    {formik.errors.lastName && formik.touched.lastName && (
                      <div className="invalid-feedback">
                        {formik.errors.lastName}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>
              <FormGroup>
                <Label
                  htmlFor="address"
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  Address
                </Label>
                <Input
                  name="address"
                  type="text"
                  className={`form-control ${
                    formik.errors.address && formik.touched.address
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("address")}
                />
                {formik.errors.address && formik.touched.address && (
                  <div className="invalid-feedback">
                    {formik.errors.address}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label
                  htmlFor="contactNumber"
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  Contact Number
                </Label>
                <Input
                  name="contactNumber"
                  type="text"
                  className={`form-control ${
                    formik.errors.contactNumber && formik.touched.contactNumber
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("contactNumber")}
                />
                {formik.errors.contactNumber &&
                  formik.touched.contactNumber && (
                    <div className="invalid-feedback">
                      {formik.errors.contactNumber}
                    </div>
                  )}
              </FormGroup>
              <FormGroup>
                <Label
                  htmlFor="email"
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  Email
                </Label>
                <Input
                  name="email"
                  type="email"
                  className={`form-control ${
                    formik.errors.email && formik.touched.email
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("email")}
                />
                {formik.errors.email && formik.touched.email && (
                  <div className="invalid-feedback">{formik.errors.email}</div>
                )}
              </FormGroup>
              <FormGroup>
                <Label
                  htmlFor="AboutMe"
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  About Me
                </Label>
                <Input
                  name="AboutMe"
                  type="textarea"
                  className={`form-control ${
                    formik.errors.AboutMe && formik.touched.AboutMe
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("AboutMe")}
                />
                {formik.errors.AboutMe && formik.touched.AboutMe && (
                  <div className="invalid-feedback">
                    {formik.errors.AboutMe}
                  </div>
                )}
              </FormGroup>
              <FormGroup>
                <Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  Vessels
                </Label>
                {formik.values.Vessels.map((vessel, index) => (
                  <div key={index} className="mb-2 d-flex align-items-center">
                    <Input
                      type="text"
                      value={vessel}
                      onChange={(e) =>
                        handleArrayChange("Vessels", index, e.target.value)
                      }
                      className="form-control me-2"
                    />
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => removeArrayItem("Vessels", index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => addArrayItem("Vessels")}
                  style={{ fontFamily: "poppins" }}
                >
                  Add Vessel
                </Button>
              </FormGroup>
              <FormGroup>
                <Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  Categories
                </Label>
                {formik.values.Categories.map((category, index) => (
                  <div key={index} className="mb-2 d-flex align-items-center">
                    <Input
                      type="text"
                      value={category}
                      onChange={(e) =>
                        handleArrayChange("Categories", index, e.target.value)
                      }
                      className="form-control me-2"
                    />
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => removeArrayItem("Categories", index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => addArrayItem("Categories")}
                  style={{ fontFamily: "poppins" }}
                >
                  Add Category
                </Button>
              </FormGroup>
              <FormGroup>
                <Label
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  Fishing Methods
                </Label>
                {formik.values.FishingMethods.map((method, index) => (
                  <div key={index} className="mb-2 d-flex align-items-center">
                    <Input
                      type="text"
                      value={method}
                      onChange={(e) =>
                        handleArrayChange(
                          "FishingMethods",
                          index,
                          e.target.value
                        )
                      }
                      className="form-control me-2"
                    />
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => removeArrayItem("FishingMethods", index)}
                    >
                      Remove
                    </Button>
                  </div>
                ))}
                <Button
                  color="secondary"
                  size="sm"
                  onClick={() => addArrayItem("FishingMethods")}
                  style={{ fontFamily: "poppins" }}
                >
                  Add Fishing Method
                </Button>
              </FormGroup>
              <FormGroup>
                <Label
                  htmlFor="VesselSafetyCertification"
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  Vessel Safety Certification (PDF, Max 10MB)
                </Label>
                <div
                  {...getSafetyRootProps()}
                  className="dropzone p-4 text-center border border-dashed rounded"
                  style={{
                    backgroundColor: "#f8f9fa",
                    cursor: "pointer",
                  }}
                >
                  <input {...getSafetyInputProps()} />
                  <p
                    className="mb-0"
                    style={{ fontFamily: "poppins", fontSize: "14px" }}
                  >
                    Drag & drop a PDF file here, or click to select
                  </p>
                </div>
                {vesselSafetyPdf ? (
                  <div className="mt-3">
                    <h6 style={{ fontFamily: "poppins", fontSize: "14px" }}>
                      Preview:
                    </h6>
                    <iframe
                      src={vesselSafetyPdf}
                      style={{
                        width: "100%",
                        height: "200px",
                        border: "1px solid #ced4da",
                      }}
                      title="Vessel Safety Certification Preview"
                    />
                    <Button
                      color="danger"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        setVesselSafetyPdf(null);
                        formik.setFieldValue("VesselSafetyCertification", "");
                      }}
                    >
                      Remove PDF
                    </Button>
                  </div>
                ) : (
                  <p
                    className="mt-2 text-muted"
                    style={{ fontFamily: "poppins", fontSize: "14px" }}
                  >
                    No Vessel Safety Certification uploaded
                  </p>
                )}
              </FormGroup>
              <FormGroup>
                <Label
                  htmlFor="EnvironmentComplianceCertificate"
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  Environment Compliance Certificate (PDF, Max 10MB)
                </Label>
                <div
                  {...getEnvRootProps()}
                  className="dropzone p-4 text-center border border-dashed rounded"
                  style={{
                    backgroundColor: "#f8f9fa",
                    cursor: "pointer",
                  }}
                >
                  <input {...getEnvInputProps()} />
                  <p
                    className="mb-0"
                    style={{ fontFamily: "poppins", fontSize: "14px" }}
                  >
                    Drag & drop a PDF file here, or click to select
                  </p>
                </div>
                {envCompliancePdf ? (
                  <div className="mt-3">
                    <h6 style={{ fontFamily: "poppins", fontSize: "14px" }}>
                      Preview:
                    </h6>
                    <iframe
                      src={envCompliancePdf}
                      style={{
                        width: "100%",
                        height: "200px",
                        border: "1px solid #ced4da",
                      }}
                      title="Environment Compliance Certificate Preview"
                    />
                    <Button
                      color="danger"
                      size="sm"
                      className="mt-2"
                      onClick={() => {
                        setEnvCompliancePdf(null);
                        formik.setFieldValue(
                          "EnvironmentComplianceCertificate",
                          ""
                        );
                      }}
                    >
                      Remove PDF
                    </Button>
                  </div>
                ) : (
                  <p
                    className="mt-2 text-muted"
                    style={{ fontFamily: "poppins", fontSize: "14px" }}
                  >
                    No Environment Compliance Certificate uploaded
                  </p>
                )}
              </FormGroup>
              <FormGroup>
                <Label
                  htmlFor="views"
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  Views
                </Label>
                <Input
                  name="views"
                  type="text"
                  className={`form-control ${
                    formik.errors.views && formik.touched.views
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("views")}
                />
                {formik.errors.views && formik.touched.views && (
                  <div className="invalid-feedback">{formik.errors.views}</div>
                )}
              </FormGroup>

              <FormGroup check>
                <Label
                  check
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  <Input
                    type="checkbox"
                    name="twoStepVerification"
                    checked={formik.values.twoStepVerification}
                    onChange={formik.handleChange}
                  />
                  Enable Two-Step Verification
                </Label>
              </FormGroup>
              <FormGroup className="d-flex align-items-center flex-wrap gap-3">
                <div>
                  <Label
                    htmlFor="profileImage"
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      fontFamily: "poppins",
                      marginBottom: "8px",
                    }}
                  >
                    Profile Image
                  </Label>
                  <div className="custom-file-upload">
                    <Input
                      id="profileImage"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                      aria-label="Upload profile image"
                    />
                    <Button
                      color="primary"
                      size="sm"
                      style={{
                        fontFamily: "poppins",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        transition:
                          "background-color 0.3s ease, transform 0.2s ease",
                      }}
                      className="custom-upload-button"
                      onClick={() =>
                        document.getElementById("profileImage").click()
                      }
                    >
                      <i className="ri-upload-2-line me-2"></i> Upload Image
                    </Button>
                  </div>
                </div>
                {profileImage ? (
                  <div className="image-preview-container mt-2">
                    <img
                      src={profileImage}
                      alt="Profile Preview"
                      style={{
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "2px solid #e9ecef",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                  </div>
                ) : (
                  <div
                    className="image-placeholder mt-2 d-flex align-items-center justify-content-center"
                    style={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "50%",
                      backgroundColor: "#f1f3f5",
                      border: "2px dashed #ced4da",
                      color: "#6c757d",
                      fontFamily: "poppins",
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  >
                    No Image
                  </div>
                )}
              </FormGroup>
              <div className="mt-4 text-end">
                <Button
                  type="submit"
                  color={submitted ? "success" : "primary"}
                  disabled={formik.isSubmitting || !userId}
                  style={{ fontFamily: "poppins" }}
                >
                  {formik.isSubmitting
                    ? "Saving..."
                    : submitted
                    ? "Saved"
                    : "Save Changes"}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>

        {/* Crop Modal */}
        <Modal isOpen={isCropModalOpen} toggle={toggleCropModal} centered>
          <ModalBody>
            <Cropper
              src={cropImage}
              style={{ height: 300, width: "100%" }}
              aspectRatio={1}
              guides={false}
              ref={cropperRef}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={handleCrop}
              style={{ fontFamily: "poppins" }}
            >
              Crop & Save
            </Button>
            <Button
              color="secondary"
              onClick={toggleCropModal}
              style={{ fontFamily: "poppins" }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </TabPane>
    </React.Fragment>
  );
};

export default BoatOwnerForm;
